import React, { useEffect, useRef } from "react"
import {
  handleGetTimeTravelThumbnails,
  handleGetTempStateIndex,
  passTimeTravelIndex,
  passSetRunMode
} from "../main2worker"
import { RUN_MODE, toHex } from "../../common/utility"
const clock = 1020488

import { useTranslation } from "../../i18n/useTranslation"

const TimeTravelPanel = () => {
  const { t } = useTranslation()
  const stateThumbRef = useRef(null)

  const convertTime = (cycleCount: number) => {
    const t = cycleCount / clock
    const min = Math.floor(t / 60)
    const sec = ("0" + (t - 60 * min).toFixed(2)).slice(-5)
    return `${("00" + min.toFixed()).slice(-3)}:${sec}`
  }

  const getTimeTravelThumbnails = () => {
    const iTempState = handleGetTempStateIndex()
    const thumbnails = handleGetTimeTravelThumbnails()
    let thumbnailText = ""
    const thumbImg = (iTempState >= 0 && thumbnails.length > 0) ?
      `${thumbnails[Math.min(iTempState, thumbnails.length - 1)].thumbnail}` : ""
    // The 170px is the width of the thumbnail images.
    const thumbImage = (thumbImg != "") ? <img style={{width: "120px"}} src={thumbImg} /> :
      <div style={{width: "120px"}}></div>
    for (let i = 0; i < thumbnails.length; i++) {
      const time = convertTime(thumbnails[i].s6502.cycleCount)
      thumbnailText += `t=${time} PC=${toHex(thumbnails[i].s6502.PC, 4)} Cycles=${thumbnails[i].s6502.cycleCount}\n`
    }
    return { iTempState, thumbnailStrings: thumbnailText.split("\n"), thumbImage }
  }

  const selectStateLine = (index: number) => {
    passTimeTravelIndex(index)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault()  // suppress normal scroll events
      const iTempState = handleGetTempStateIndex()
      const thumbnails = handleGetTimeTravelThumbnails()
      let index = iTempState
      const delta = e.metaKey ? 100 : (e.ctrlKey ? 10 : 1)
      index = (e.key === "ArrowUp") ? index - delta : index + delta
      index = Math.max(0, Math.min(thumbnails.length - 1, index))
      if (index !== iTempState) {
        passTimeTravelIndex(index)
      }
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      passSetRunMode(RUN_MODE.RUNNING)
    }
  }

  const { iTempState, thumbnailStrings, thumbImage } = getTimeTravelThumbnails()

  // Make sure our new snapshot is visible in the scroll range. This has to run
  // after the highlighted line has been committed to the DOM, so it lives in an
  // effect; getTimeTravelThumbnails() runs during render and must not touch a ref.
  useEffect(() => {
    const timeout = setTimeout(() => {
      const lineToScrollTo = document.getElementById("tempStateIndex")
      if (lineToScrollTo && stateThumbRef.current) {
        const div: HTMLDivElement = stateThumbRef.current
        // Calculate the scroll position to make the line visible
        const containerRect = div.getBoundingClientRect()
        const targetRect = lineToScrollTo.getBoundingClientRect()
        const isTargetAboveViewport = targetRect.top < containerRect.top
        const isTargetBelowViewport = targetRect.bottom > containerRect.bottom
        if (isTargetAboveViewport || isTargetBelowViewport) {
          div.scrollTop = targetRect.top - containerRect.top + div.scrollTop
        }
      }
    }, 50)
    return () => clearTimeout(timeout)
  }, [iTempState, thumbnailStrings.length])

  let timeTravelThumbnails = <></>

  if (thumbnailStrings.length > 1) {
    timeTravelThumbnails = <>{thumbnailStrings.map((line, index) => (
      <div key={index}
        id={index === iTempState ? "tempStateIndex" : ""}
        className={"stateLine" + (index === iTempState ? " program-counter" : "")}
        onClick={() => selectStateLine(index)}>
        {line}
      </div>
    ))}
    </>
  }

  // Need to set tabIndex={-1} on the div to get onKeyDown to work.
  // Could change to tabIndex={0} to make the div part of the tab order.
  return (
    <div className="round-rect-border short-panel" style={{ height: "120px" }}>
      <div className="bigger-font column-gap">{t("debug.timeTravelSnapshots")}</div>
      <div className="flex-row">
        <div ref={stateThumbRef} className="thin-border debug-panel mono-text"
          onKeyDown={(e) => handleKeyDown(e)}
          tabIndex={-1}
          style={{
            width: "25em",
            height: "72pt",
            overflow: "auto",
            cursor: "pointer",
            userSelect: "none",
          }}>
          {timeTravelThumbnails}
        </div>
        <div
          style={{
            marginTop: "5px",
            marginLeft: "5px",
          }}>
          {thumbImage}
        </div>
      </div>
    </div>
  )
}

export default TimeTravelPanel
